import { useCallback, lazy, Suspense } from 'react'

import { Button, Skeleton } from 'antd'
import { Icon } from 'govwell-ui'
import type { SyntaxHighlighterProps } from 'react-syntax-highlighter'
import styled from 'styled-components'

import { getFontSize, TextSize } from 'src/components/Typography/Text'
import useDisclosure from 'src/hooks/use-disclosure'

const SyntaxHighlighter = lazy(async () => {
  const { Prism } = await import('react-syntax-highlighter')
  return { default: Prism as unknown as React.ComponentType<SyntaxHighlighterProps> }
})

const Container = styled.div`
  position: relative;
  padding: 12px 24px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colorBgLayout};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

type Props = {
  code: string
}
const CodeSnippet = ({ code }: Props) => {
  const { isOpen: isCopying, open: startCopying, close: stopCopying } = useDisclosure()
  const handleCopyClick = useCallback(async () => {
    startCopying()
    await navigator.clipboard.writeText(code)
    setTimeout(() => {
      stopCopying()
    }, 1000)
  }, [code, startCopying, stopCopying])

  return (
    <Suspense fallback={<Skeleton active />}>
      <Container>
        <SyntaxHighlighter
          language="html"
          customStyle={{
            backgroundColor: 'transparent',
            padding: 0,
            fontSize: getFontSize(TextSize.Base),
          }}
          wrapLines
          wrapLongLines
          lineProps={{
            style: { flexWrap: 'wrap', wordBreak: 'break-word', whiteSpace: 'pre-wrap' },
          }}
        >
          {code}
        </SyntaxHighlighter>
        <Button icon={isCopying ? <Icon.FaCheck /> : <Icon.FaCopy />} onClick={handleCopyClick}>
          {isCopying ? 'Copied!' : 'Copy'}
        </Button>
      </Container>
    </Suspense>
  )
}

export default React.memo(CodeSnippet)
